import { isGarminReturnUrl, isMutualCustomerIdentifier } from "@ca/types/garmin";

export type Handshake = {
	mci: string;
	/** Decoded and verified to land back on Garmin. Null when Garmin sent us none. */
	returnUrl: string | null;
	/** Set when a returnUrl arrived but failed the Garmin-host check, so the page can say so. */
	rejectedReturnUrl: string | null;
	/** The query string exactly as it arrived, for the server to log. */
	rawQuery: string;
};

// Garmin documents `returnUrl`; read it case-insensitively and under the snake_case spelling
// too, so a spelling drift on their side degrades to a logged mismatch rather than a dead end.
const RETURN_URL_KEYS = ["returnurl", "return_url", "redirecturl", "redirect_url"];

const findParam = (params: URLSearchParams, keys: readonly string[]): string | null => {
	for (const [key, value] of params) {
		if (keys.includes(key.toLowerCase())) {
			return value;
		}
	}
	return null;
};

/** Garmin's example is encoded once; decode until stable so a double-encoded value still resolves. */
const fullyDecoded = (value: string): string => {
	let current = value;
	for (let i = 0; i < 3; i++) {
		let next: string;
		try {
			next = decodeURIComponent(current);
		} catch {
			return current;
		}
		if (next === current) {
			return current;
		}
		current = next;
	}
	return current;
};

const withScheme = (value: string): string =>
	(value === "" || /^[a-z][a-z0-9+.-]*:/i.test(value) ? value : `https://${value}`);

/**
 * Null when this is an ordinary visit rather than a connection attempt, so the page can fall
 * back to the marketing view.
 */
export const readHandshake = (search: string): Handshake | null => {
	const params = new URLSearchParams(search);
	const mci = (findParam(params, ["mutualcustomeridentifier", "mci"]) ?? "").trim();
	if (!isMutualCustomerIdentifier(mci)) {
		return null;
	}
	const raw = findParam(params, RETURN_URL_KEYS);
	// flyGarmin sends the return link without a scheme ("fg.garmin.com/aircraft-logbook/...").
	const decoded = raw ? withScheme(fullyDecoded(raw).trim()) : null;
	const accepted = decoded !== null && decoded !== "" && isGarminReturnUrl(decoded);
	return {
		mci,
		returnUrl: accepted ? decoded : null,
		rejectedReturnUrl: decoded && !accepted ? decoded : null,
		rawQuery: search,
	};
};

/**
 * Hand the browser back to Garmin. Must be a real navigation — Garmin only finalizes the
 * connection when the user's authenticated session makes the request.
 */
export const returnToGarmin = (returnUrl: string) => {
	window.location.assign(returnUrl);
};
